<template>
  <div class="rb-screening-s2">
    <div class="rb-ob-conditional" :class="{ 'is-open': !!intentKey }">
      <div class="rb-ob-conditional-inner">
        <template v-if="intentKey && ageMode === 'MULTI_SELECT_POPULATION_BANDS'">
          <p v-if="agePrompt" class="rb-ob-q">{{ agePrompt }}</p>
          <div class="rb-ob-choice-grid">
            <div
                v-for="opt in ageBandOptions"
                :key="opt.id"
                class="rb-ob-choice"
                :class="{ 'is-selected': multiValues.indexOf(opt.id) !== -1 }"
            >
              <label class="rb-ob-choice-select">
                <input
                    type="checkbox"
                    :value="opt.id"
                    :checked="multiValues.indexOf(opt.id) !== -1"
                    @change="toggleAgeBand(opt)"
                >
                <span class="rb-ob-choice-body">
                  <span class="rb-ob-choice-title">{{ opt.label || opt.id }}</span>
                </span>
              </label>
            </div>
          </div>
        </template>

        <template v-else-if="intentKey && ageMode === 'FIXED_AGE_LIST' && ageFields.length > 1">
          <p v-if="agePrompt" class="rb-ob-q">{{ agePrompt }}</p>
          <div class="rb-ob-age-pair">
            <div
                v-for="field in ageFields"
                :key="field.role"
                class="rb-ob-field-block"
            >
              <label :for="'age-' + field.role">{{ field.label || agePrompt || 'سن' }}</label>
              <input
                  :id="'age-' + field.role"
                  v-model="ageByRole[field.role]"
                  class="rb-ob-control"
                  type="text"
                  inputmode="numeric"
                  autocomplete="off"
                  :placeholder="agePlaceholder"
                  @input="emitChange"
              >
            </div>
          </div>
        </template>

        <template v-else-if="intentKey">
          <div
              v-for="field in ageFields"
              :key="field.role"
              class="rb-ob-field-block"
          >
            <label class="rb-ob-q" :for="'age-' + field.role">{{ field.role === 'SELF_PARTICIPANT' ? $t('intake.screeningUi.ageLabel') : (field.label || agePrompt || promptFallback) }}</label>
            <input
                :id="'age-' + field.role"
                v-model="ageByRole[field.role]"
                class="rb-ob-control"
                type="text"
                inputmode="numeric"
                autocomplete="off"
                :placeholder="agePlaceholder"
                :aria-invalid="ageError(field) ? 'true' : 'false'"
                :aria-describedby="ageError(field) ? 'age-error-' + field.role : null"
                @input="emitChange"
            >
            <p v-if="ageError(field)" :id="'age-error-' + field.role" role="status">{{ ageError(field) }}</p>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import {parseAgeInput} from '@/intake/screeningServiceUi'

export default {
  name: 'ScreeningAgeInputs',
  props: {
    question: {
      type: Object,
      required: true,
    },
    s1Intent: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      multiValues: [],
      ageByRole: {},
    }
  },
  computed: {
    intentKey() {
      return this.s1Intent
        || (this.question && this.question.s1_intent)
        || null
    },
    agePresentation() {
      const map = (this.question && this.question.presentation_by_intent) || {}
      if (this.intentKey && map[this.intentKey]) {
        return map[this.intentKey]
      }
      return null
    },
    ageMode() {
      return (this.agePresentation && this.agePresentation.mode) || 'SINGLE_AGE'
    },
    agePrompt() {
      return (this.agePresentation && this.agePresentation.prompt) || ''
    },
    ageFields() {
      return (this.agePresentation && Array.isArray(this.agePresentation.fields))
        ? this.agePresentation.fields
        : []
    },
    ageBandOptions() {
      if (this.agePresentation && Array.isArray(this.agePresentation.options)) {
        return this.agePresentation.options
      }
      return []
    },
    promptFallback() {
      return (this.question && this.question.text) || 'سن'
    },
    agePlaceholder() {
      try {
        return this.$t('intake.agePlaceholder')
      } catch (e) {
        return ''
      }
    },
  },
  watch: {
    question: {
      immediate: true,
      handler() {
        this.resetLocal()
      },
    },
    s1Intent() {
      this.resetLocal()
    },
  },
  methods: {
    ageError(field) {
      if (!String(this.ageByRole[field.role] || '').trim()) return ''
      const age = parseAgeInput(this.ageByRole[field.role])
      const min = field.min != null ? Number(field.min) : 0
      const max = field.max != null ? Number(field.max) : 120
      if (age != null && age >= min && age <= max) return ''
      return (this.$route && this.$route.params.lang === 'en')
        ? `Enter an age between ${min} and ${max} for this path.`
        : `سن در این مسیر باید بین ${min.toLocaleString('fa-IR')} و ${max.toLocaleString('fa-IR')} سال باشد؛ در صورت نیاز مسیر مناسب سن را انتخاب کن.`
    },
    resetLocal() {
      const prefill = this.question.answer_prefill || {}
      this.multiValues = (prefill.answer_ids || []).slice()
      const ages = {}
      this.ageFields.forEach((f) => {
        const saved = (prefill.ages || []).find((item) => item.role === f.role)
        ages[f.role] = saved ? String(saved.age) : ''
      })
      this.ageByRole = ages
      this.emitChange()
    },
    toggleAgeBand(opt) {
      const id = opt.id
      if (opt.exclusive || id === 'ADULTS_ONLY') {
        this.multiValues = this.multiValues.indexOf(id) !== -1 ? [] : [id]
        this.emitChange()
        return
      }
      let next = this.multiValues.filter((x) => x !== 'ADULTS_ONLY')
      const idx = next.indexOf(id)
      if (idx === -1) {
        next.push(id)
      } else {
        next.splice(idx, 1)
      }
      this.multiValues = next
      this.emitChange()
    },
    buildPayload() {
      const required = !(this.question && this.question.required === false)
      if (!this.intentKey) {
        return {valid: !required, answer_ids: []}
      }
      if (this.ageMode === 'MULTI_SELECT_POPULATION_BANDS') {
        return {
          valid: this.multiValues.length > 0,
          answer_ids: this.multiValues.slice(),
          ages: [],
        }
      }
      const ages = []
      let valid = true
      this.ageFields.forEach((field) => {
        const age = parseAgeInput(this.ageByRole[field.role])
        if (age == null) {
          valid = false
          return
        }
        const min = field.min != null ? Number(field.min) : 0
        const max = field.max != null ? Number(field.max) : 120
        if (age < min || age > max) {
          valid = false
        }
        ages.push({role: field.role, age})
      })
      return {
        valid: valid && ages.length > 0,
        answer_ids: [],
        ages,
      }
    },
    emitChange() {
      this.$emit('change', this.buildPayload())
    },
    getAnswer() {
      return this.buildPayload()
    },
  },
}
</script>
